import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Alert, Snackbar } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import SplitPaneMAIQ from 'app/components/SplitPaneMAIQ';
import OperationsPanel from './OperationsPanel';
import ChatPanel from './ChatPanel';
import ChatHistory from './ChatHistoryBar';
import EmailContentPreview from './EmailContent/EmailContentPreview';
import { 
  ChatState, 
  EmailContent, 
  FlowStep, 
  GenerateFlowProps,
  ProcessingStatus
} from '../../types';
import { useMiaChatV2 } from 'app/context/MiaChatV2Context';
import { ChatResponse, Suggestion } from 'app/apiService/miaChat/types';
import { Message } from '../../types';
import MiaChatV2ApiService from 'app/apiService/miaChat/miaChatV2ApiService';
import { miaChatV2SocketService, MiaChatV2Event } from 'app/socketService/miaChatV2/miaChatV2SocketService';
import { PromptSelection } from './OperationsPanel/types';

const GenerateTab: React.FC<GenerateFlowProps> = ({ dealerId }) => {
  const selectedDealer = useSelector(selectDealerSelected);
  const currentDealerId = dealerId || selectedDealer?.dealer || '';
  
  // Use MiaChatV2Context instead of local state
  const {
    loading,
    setLoading,
    error,
    setError,
    currentConversation,
    setCurrentConversation,
    initChat,
    startNewChat,
    sendChatMessage,
    updateConversationState
  } = useMiaChatV2();
  
  const initialChatState: ChatState = {
    step: 0,
    chatHistory: [],
    emailContent: { subject: '', sections: [] },
    activeTab: "Generate",
    showPreview: false,
    inputText: "",
    loading: false,
    error: null,
    suggestions: [],
    next_action: '',
    is_input_restricted_to_suggestions: false
  }

  // Chat state management
  const [chatState, setChatState] = useState<ChatState>(initialChatState);

  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showEditableEmail, setShowEditableEmail] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState<{
    html: string;
    subject: string;
    conversation_id: string;
  } | null>(null);
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  
  // State to track if chat inputs should be disabled (during email generation and template save + PUT API call)
  const [isChatInputDisabled, setIsChatInputDisabled] = useState(false);

  // Streaming data state for GenerationProgressPopupMAIQ component
  const [streamingData, setStreamingData] = useState<any>(null);
  const [streamingText, setStreamingText] = useState<string>('');
  const [currentSection, setCurrentSection] = useState<{index: number; type: string; main?: string; sub_text?: string} | null>(null);

  // Chat history management
  const [chatHistory, setChatHistory] = useState<Array<{
    id: string;
    title: string;
    timestamp: Date;
    type: 'email' | 'general' | 'template';
  }>>([]);
  
  const [activeConversationId, setActiveConversationId] = useState<string>('');
  const [chatStates, setChatStates] = useState<Record<string, ChatState>>({});

  // Socket service refs and state
  const socketServiceRef = useRef<any>(null);
  const [isSocketConnected, setIsSocketConnected] = useState<boolean>(false);
  
  // API call tracking state - to control when socket messages should be shown
  const [isApiCallInProgress, setIsApiCallInProgress] = useState<boolean>(false);
  
  // AbortController for all chat-related API calls
  const emailGenerationAbortControllerRef = useRef<AbortController | null>(null);
  const initChatAbortControllerRef = useRef<AbortController | null>(null);
  const startNewChatAbortControllerRef = useRef<AbortController | null>(null);
  const sendChatMessageAbortControllerRef = useRef<AbortController | null>(null);
  const updateConversationStateAbortControllerRef = useRef<AbortController | null>(null);

  // Initialize socket service
  useEffect(() => {
    try {
      const wsUrl = process.env.REACT_APP_WS_URL;
      if (wsUrl) {
        const config = {
          url: wsUrl,
          autoConnect: true,
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          options: {
            path: '/ws/',
            transports: ['websocket', 'polling']
          }
        };
        
        socketServiceRef.current = miaChatV2SocketService(config);
        socketServiceRef.current.connect();
        setIsSocketConnected(socketServiceRef.current.isConnected());
        
        console.log('🔌 MiaChatV2 Socket Service initialized');
      } else {
        console.warn('⚠️ WebSocket URL not configured');
      }
    } catch (error) {
      console.error('❌ Failed to initialize socket service:', error);
    }

    return () => {
      // Cancel ALL ongoing API calls on component unmount
      const abortControllers = [
        { ref: emailGenerationAbortControllerRef, name: 'email generation' },
        { ref: initChatAbortControllerRef, name: 'init chat' },
        { ref: startNewChatAbortControllerRef, name: 'start new chat' },
        { ref: sendChatMessageAbortControllerRef, name: 'send chat message' },
        { ref: updateConversationStateAbortControllerRef, name: 'update conversation state' }
      ];
      
      abortControllers.forEach(({ ref, name }) => {
        if (ref.current) {
          ref.current.abort();
          ref.current = null;
        }
      });
      
      if (socketServiceRef.current) {
        // Unsubscribe from current conversation before disconnecting
        if (activeConversationId) {
          socketServiceRef.current.unsubscribeFromConversation(activeConversationId);
        }
        socketServiceRef.current.disconnect();
      }
    };
  }, []);

  // Socket event handler - use refs to avoid dependency issues
  const activeConversationIdRef = useRef(activeConversationId);
  const isApiCallInProgressRef = useRef(isApiCallInProgress);
  
  // Update refs when values change
  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);
  
  useEffect(() => {
    isApiCallInProgressRef.current = isApiCallInProgress;
  }, [isApiCallInProgress]);

  const handleSocketEvent = useCallback((eventData: MiaChatV2Event) => {
    
    // Only show socket messages when an API call is in progress
    if (!isApiCallInProgressRef.current) {
      return;
    }

    // Handle processing_status events with tool call processing_type
    const message = eventData.message || (eventData as any).message;
    const processingType = message?.processing_type;
    
    if (message && processingType) {
      if (processingType) {
        const _message: Message = {
          role: 'system',
          content: message.text,
          id: `_message_${Date.now()}_${Math.random()}`,
          timestamp: eventData.timestamp || new Date().toISOString(),
          bgcolor: processingType.includes('completed') ? '#d4edda' : '#fff3cd', // Yellow for executing, green for completed
          processingType: processingType
        };

        // Add message to chat history (avoid duplicates)
        setChatState(prev => {
          // Check if message with same content already exists
          const exists = prev.chatHistory.some(msg => 
            msg.role === 'system' && 
            msg.content === _message.content && 
            msg.processingType === _message.processingType
          );
          
          if (exists) return prev;
          
          return {
            ...prev,
            chatHistory: [...prev.chatHistory, _message]
          };
        });

        // Also update context conversation if available
        if (currentConversation) {
          setCurrentConversation({
            ...currentConversation,
            messages: [...(currentConversation.messages || []), _message]
          });
        }
      }
    }

    // Handle direct tool call events (keeping for backward compatibility)
    if (eventData.tool_name) {
      const _message: Message = {
        role: 'system',
        content: eventData.message?.content || '',
        id: `tool_${Date.now()}_${Math.random()}`,
        timestamp: eventData.timestamp || new Date().toISOString(),
        bgcolor: eventData.tool_name.includes('completed') ? '#d4edda' : '#fff3cd', // Yellow for executing, green for completed
        processingType: eventData.processing_type || 'processing'
      };

      // Add tool message to chat history (avoid duplicates)
      setChatState(prev => {
        // Check if message with same content already exists
        const exists = prev.chatHistory.some(msg => 
          msg.role === 'system' && 
          msg.content === _message.content && 
          msg.processingType === _message.processingType
        );
        
        if (exists) return prev;
        
        return {
          ...prev,
          chatHistory: [...prev.chatHistory, _message]
        };
      });

      // Also update context conversation if available
      if (currentConversation) {
        setCurrentConversation({
          ...currentConversation,
          messages: [...(currentConversation.messages || []), _message]
        });
      }
    }

    // Handle streaming section response data
    if (eventData.message && (eventData.message as any).sections && (eventData.message as any).text) {
      setStreamingData(eventData);
      
      // Update current section if available
      if ((eventData.message as any).sections && (eventData.message as any).sections.length > 0) {
        const currentSectionData = (eventData.message as any).sections.find((section: any) => 
          section.index === (eventData.message as any).text?.key?.index
        );
        if (currentSectionData) {
          setCurrentSection({
            index: currentSectionData.index,
            type: currentSectionData.type,
            main: currentSectionData.main || '',
            sub_text: currentSectionData.sub_text || ''
          });
        }
      }
    }
  }, []); // Remove dependencies to prevent callback recreation

  // Subscribe to conversation when activeConversationId changes
  useEffect(() => {
    if (socketServiceRef.current && activeConversationId) {
      socketServiceRef.current.subscribeToConversation(
        activeConversationId,
        handleSocketEvent
      );
    }

    return () => {
      if (socketServiceRef.current && activeConversationId) {
        socketServiceRef.current.unsubscribeFromConversation(activeConversationId);
      }
    };
  }, [activeConversationId]); // Remove handleSocketEvent dependency

  // Update chat state safely and sync with context
  const updateChatState = useCallback((updates: Partial<ChatState>) => {
    setChatState(prev => {
      const newState = { ...prev, ...updates };
      return newState;
    });
    
    // Sync loading state with context
    if (updates.loading !== undefined) {
      setLoading(updates.loading);
    }
    
    // Sync error state with context
    if (updates.error !== undefined) {
      setError(updates.error || '');
    }
  }, [chatState, setLoading, setError]);

  // Function to detect if email generation is ready
  const isEmailGenerationReady = useCallback((messages: Message[], next_action, terminal_action) => {
    if (!messages || messages.length === 0) return false;
    
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.role !== 'assistant') return false;
    
    const content = lastMessage.content.toLowerCase();
    const isReady = (next_action === 'GENERATE_EMAIL') || content.includes('generate email');
    
    return isReady;
  }, []);

  // Function to call generateEmail API
  const generateEmailFromConversation = useCallback(async (messages: Message[], conversationId: string) => {
    try {
      if (!currentDealerId || !conversationId) {
        return;
      }

      // Create new AbortController for this email generation
      const abortController = new AbortController();
      emailGenerationAbortControllerRef.current = abortController;
      
      // Set email generation state to true AND disable chat inputs
      setIsGeneratingEmail(true);
      setIsChatInputDisabled(true);
      
      const response = await MiaChatV2ApiService.generateEmail({
        dealer_id: currentDealerId,
        conversation_id: conversationId,
        messages: messages,
        action: 'GENERATE_EMAIL'
      }, abortController.signal);

      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }

      if (response.body.generatedHtml) {
        setGeneratedEmail({
          html: response.body.generatedHtml,
          subject: response.body.subject,
          conversation_id: response.body.conversation_id
        });
        setSuccessMessage('Email generated successfully! Save as template to continue.');
      }
    } catch (error) {
      // Check if error is due to abort
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      
      console.error('Error generating email:', error);
      const contextError = error instanceof Error ? error.message : 'Failed to generate email. Please try again.';
      updateChatState({ error: contextError });
    } finally {
      // Always set email generation state to false and re-enable chat inputs when done
      setIsGeneratingEmail(false);
      setIsChatInputDisabled(false);
      // Clear the abort controller reference
      emailGenerationAbortControllerRef.current = null;
    }
  }, [currentDealerId, updateChatState]);

  const handleOperationSelect = useCallback(async (prompt: PromptSelection) => {
    try {
      const operation = prompt.action || prompt.title; //prefer the action over title if button is clicked
      const title = prompt.title;
      // Create immediate user message for selected operation
      const userMessage = { 
        role: "user" as const, 
        content: title,
        id: `user_${Date.now()}_${Math.random()}`,
        timestamp: new Date().toISOString()
      };

      // Immediately update state to show chat panel with user message
      const immediateState: ChatState = {
        step: 1 as FlowStep, // Move to step 1 to show chat panel immediately
        chatHistory: [userMessage], // Show user message immediately
        emailContent: { subject: '', sections: [] },
        activeTab: "Generate",
        showPreview: false,
        inputText: "",
        loading: true, // Show loading spinner in chat panel
        error: null,
        suggestions: [],
        next_action: '',
        is_input_restricted_to_suggestions: false
      };

      updateChatState(immediateState);
      
      // Set API call in progress state
      setIsApiCallInProgress(true);
      
      // Step 1: First initialize chat to get conversation_id
      
      // Create AbortController for initChat
      const initAbortController = new AbortController();
      initChatAbortControllerRef.current = initAbortController;
      
      const initResponse = await initChat(initAbortController.signal);
      
      if (!initResponse || !initResponse.conversation_id) {
        throw new Error('Failed to initialize chat - no conversation ID received');
      }
      
      setActiveConversationId(initResponse.conversation_id);
      
      // Create AbortController for startNewChat
      const startNewChatAbortController = new AbortController();
      startNewChatAbortControllerRef.current = startNewChatAbortController;
      
      const response = await startNewChat(operation, initResponse.conversation_id, startNewChatAbortController.signal);
      
      if (response) {
        // Update email content if available
        let updatedEmailContent = { ...chatState.emailContent };
        if (response.email_content_preview && response.email_content_preview.length > 0) {
          const preview = response.email_content_preview[0];
          updatedEmailContent = {
            ...updatedEmailContent,
            subject: preview.subject,
            sections: preview.sections,
          };
        }

        const updatedState: ChatState = {
          step: 1 as FlowStep,
          chatHistory: response.messages || [userMessage], // Use API messages or fallback to user message
          emailContent: response.email_content_preview && response.email_content_preview.length > 0 
            ? response.email_content_preview[0] 
            : { subject: '', sections: [] },
          activeTab: "Generate",
          showPreview: false,
          inputText: "",
          loading: false, // Stop loading after API response
          error: null,
          suggestions: response.suggestions || [],
          next_action: response.next_action,
          is_input_restricted_to_suggestions: response.is_input_restricted_to_suggestions || false
        };

        updateChatState(updatedState);
        
        // Save state for this conversation using the init conversation ID
        setChatStates(prev => ({
          ...prev,
          [initResponse.conversation_id]: updatedState
        }));
        
      }
    } catch (error) {
      const contextError = error instanceof Error ? error.message : 'Failed to start operation. Please try again.';
      updateChatState({ 
        loading: false, 
        error: contextError
      });
    } finally {
      // Always clear API call in progress state when operation completes
      setIsApiCallInProgress(false);
    }
  }, [chatState, updateChatState, initChat, startNewChat]);

  // Handle option selection
  const handleOptionSelect = useCallback(async (option: Suggestion) => {
    try {
      // First add user message to chat history immediately
      const userMessage = { role: "user" as const, content: option.text };
      const updatedMessages = [...chatState.chatHistory, userMessage];
      
      updateChatState({ 
        loading: true,
        chatHistory: updatedMessages // Show user message immediately
      });

      // Set API call in progress state
      setIsApiCallInProgress(true);

      if (currentDealerId && activeConversationId) {
        // Create AbortController for sendChatMessage
        const sendChatAbortController = new AbortController();
        sendChatMessageAbortControllerRef.current = sendChatAbortController;
        
        const response = await sendChatMessage({
          dealer_id: currentDealerId,
          conversation_id: activeConversationId,
          selected_option: option.id,
          messages: updatedMessages, // Use updated messages with user message
        }, sendChatAbortController.signal);

        if (response) {
          const { suggestions, email_content_preview, next_action, messages, is_input_restricted_to_suggestions, terminal_action } = response;
          
          const updatedState: ChatState = {
            step: Math.min(chatState.step + 1, 6) as FlowStep,
            chatHistory: messages, // Use complete messages array from API
            emailContent: email_content_preview && email_content_preview.length > 0 
              ? email_content_preview[0] 
              : chatState.emailContent,
            activeTab: chatState.activeTab,
            showPreview: chatState.showPreview,
            inputText: chatState.inputText,
            loading: false,
            error: null,
            suggestions: suggestions || [], // Add suggestions from API response
            next_action: next_action,
            is_input_restricted_to_suggestions: is_input_restricted_to_suggestions || false,
            terminal_action: terminal_action || false
          };

          updateChatState(updatedState);
          setChatStates(prev => ({
            ...prev,
            [activeConversationId]: { ...prev[activeConversationId], ...updatedState }
          }));

          // Check if email generation is ready and trigger API call
          if (isEmailGenerationReady(messages, next_action, terminal_action)) {
            await generateEmailFromConversation(messages, activeConversationId);
          }
        }
      }
    } catch (error) {
      console.error('Error handling option select:', error);
      const contextError = error instanceof Error ? error.message : 'Failed to process your selection. Please try again.';
      updateChatState({ 
        loading: false, 
        error: contextError
      });
    } finally {
      // Always clear API call in progress state when option selection completes
      setIsApiCallInProgress(false);
    }
  }, [chatState, updateChatState, currentDealerId, activeConversationId, sendChatMessage, isEmailGenerationReady, generateEmailFromConversation]);

  // Handle send message
  const handleSendMessage = useCallback(async (message: string) => {
    try {
      // First add user message to chat history immediately
      const userMessage = { role: "user" as const, content: message };
      const updatedMessages = [...chatState.chatHistory, userMessage];
      
      updateChatState({ 
        loading: true,
        chatHistory: updatedMessages, // Show user message immediately
        inputText: "", // Clear input text immediately
        step: 1 as FlowStep // Move to step 1 to show chat panel
      });

      // Set API call in progress state
      setIsApiCallInProgress(true);

      // If no active conversation, initialize chat first
      let conversationId = activeConversationId;
      if (!conversationId && currentDealerId) {
        // Create AbortController for initChat
        const initAbortController = new AbortController();
        initChatAbortControllerRef.current = initAbortController;
        
        const initResponse = await initChat(initAbortController.signal);
        
        if (!initResponse || !initResponse.conversation_id) {
          throw new Error('Failed to initialize chat - no conversation ID received');
        }
        
        conversationId = initResponse.conversation_id;
        setActiveConversationId(conversationId);
      }

      if (currentDealerId && conversationId) {
        // Create AbortController for sendChatMessage
        const sendChatAbortController = new AbortController();
        sendChatMessageAbortControllerRef.current = sendChatAbortController;
        
        const response = await sendChatMessage({
          dealer_id: currentDealerId,
          conversation_id: conversationId,
          messages: updatedMessages, // Use updated messages with user message
        }, sendChatAbortController.signal);

        if (response) {
          const { suggestions, email_content_preview, messages, is_input_restricted_to_suggestions, next_action, terminal_action } = response;
          
          // Replace old chat with new messages from API response
          const updatedState: ChatState = {
            step: Math.min(chatState.step + 1, 6) as FlowStep,
            chatHistory: messages, // Use complete messages array from API
            emailContent: email_content_preview && email_content_preview.length > 0 
              ? email_content_preview[0] 
              : chatState.emailContent,
            activeTab: chatState.activeTab,
            showPreview: chatState.showPreview,
            inputText: "", // Keep input text cleared
            loading: false,
            error: null,
            suggestions: suggestions || [], // Add suggestions from API response
            next_action: response.next_action,
            is_input_restricted_to_suggestions: is_input_restricted_to_suggestions || false,
            terminal_action: response.terminal_action || false
          };

          updateChatState(updatedState);
          setChatStates(prev => ({
            ...prev,
            [conversationId]: { ...prev[conversationId], ...updatedState }
          }));

          // Check if email generation is ready and trigger API call
          if (isEmailGenerationReady(messages, next_action, terminal_action)) {
            await generateEmailFromConversation(messages, conversationId);
          }
        }
      }
    } catch (error) {
      const contextError = error instanceof Error ? error.message : 'Failed to send message. Please try again.';
      updateChatState({ 
        loading: false, 
        error: contextError
      });
    } finally {
      // Always clear API call in progress state when message sending completes
      setIsApiCallInProgress(false);
    }
  }, [chatState.chatHistory, chatState.step, updateChatState, currentDealerId, activeConversationId, initChat, sendChatMessage, isEmailGenerationReady, generateEmailFromConversation]);

  
  // Chat history handlers
  const handleNewConversation = useCallback(() => {
    // Unsubscribe from current conversation before starting new one
    if (socketServiceRef.current && activeConversationId) {
      console.log('🔄 Unsubscribing from conversation before starting new one:', activeConversationId);
      socketServiceRef.current.unsubscribeFromConversation(activeConversationId);
    }
    
    const newId = `conv_${Date.now()}`;
    const newConversation = {
      id: newId,
      title: 'New Chat',
      timestamp: new Date(),
      type: 'general' as const,
    };
    
    setChatHistory(prev => [newConversation, ...prev]);
    setActiveConversationId(newId);
    
    setChatState(initialChatState);
    setChatStates(prev => ({
      ...prev,
      [newId]: initialChatState
    }));
  }, [activeConversationId]);

  const handleConversationSelect = useCallback((id: string) => {
    // Cancel ALL ongoing API calls when switching conversations
    const abortControllers = [
      { ref: emailGenerationAbortControllerRef, name: 'email generation' },
      { ref: initChatAbortControllerRef, name: 'init chat' },
      { ref: startNewChatAbortControllerRef, name: 'start new chat' },
      { ref: sendChatMessageAbortControllerRef, name: 'send chat message' },
      { ref: updateConversationStateAbortControllerRef, name: 'update conversation state' }
    ];
    
    abortControllers.forEach(({ ref, name }) => {
      if (ref.current) {
        ref.current.abort();
        ref.current = null;
      }
    });
    
    // Clear all streaming and temporary states when switching conversations
    setStreamingData(null);
    setStreamingText('');
    setCurrentSection(null);
    setIsApiCallInProgress(false);
    setSuccessMessage('');
    setIsGeneratingEmail(false);
    setGeneratedEmail(null);
    setShowEditableEmail(false);
    setIsChatInputDisabled(false);
    
    setActiveConversationId(id);
    const selectedConversation = chatHistory.find(conv => conv.id === id);
    
    if (selectedConversation) {
      const savedState = chatStates[id];
      if (savedState) {
        setChatState(savedState);
      } else {
        setChatState(initialChatState);
      }
    }
  }, [chatHistory, chatStates]);

  const handleDeleteConversation = useCallback((id: string) => {
    // Unsubscribe from conversation if it's being deleted
    if (socketServiceRef.current && activeConversationId === id) {
      socketServiceRef.current.unsubscribeFromConversation(id);
    }
    
    setChatHistory(prev => prev.filter(conv => conv.id !== id));
    setChatStates(prev => {
      const newStates = { ...prev };
      delete newStates[id];
      return newStates;
    });
    
    if (activeConversationId === id) {
      setActiveConversationId('');
      setChatState(initialChatState);
    }
  }, [activeConversationId]);

  const handleClearHistory = useCallback(() => {
    // Unsubscribe from current conversation before clearing history
    if (socketServiceRef.current && activeConversationId) {
      console.log('🔄 Unsubscribing from conversation before clearing history:', activeConversationId);
      socketServiceRef.current.unsubscribeFromConversation(activeConversationId);
    }
    
    setChatHistory([]);
    setActiveConversationId('');
    setChatStates({});
    setChatState(initialChatState);
  }, []);

  const handleReset = useCallback(() => {
    const resetState = initialChatState;
    
    // Cancel ALL ongoing API calls
    const abortControllers = [
      { ref: emailGenerationAbortControllerRef, name: 'email generation' },
      { ref: initChatAbortControllerRef, name: 'init chat' },
      { ref: startNewChatAbortControllerRef, name: 'start new chat' },
      { ref: sendChatMessageAbortControllerRef, name: 'send chat message' },
      { ref: updateConversationStateAbortControllerRef, name: 'update conversation state' }
    ];
    
    abortControllers.forEach(({ ref, name }) => {
      if (ref.current) {
        ref.current.abort();
        ref.current = null;
      }
    });
    
    // Unsubscribe from current conversation before resetting
    if (socketServiceRef.current && activeConversationId) {
      socketServiceRef.current.unsubscribeFromConversation(activeConversationId);
    }
    
    // Reset all chat states
    setChatState(resetState);
    setSuccessMessage('');
    setIsGeneratingEmail(false);
    setGeneratedEmail(null);
    
    // Reset email generation related states
    setShowEditableEmail(false);
    setIsChatInputDisabled(false);
    
    // Reset streaming data states
    setStreamingData(null);
    setStreamingText('');
    setCurrentSection(null);
    
    // Reset API call tracking state
    setIsApiCallInProgress(false);
    
    // Reset conversation states if active
    if (activeConversationId) {
      setChatStates(prev => ({
        ...prev,
        [activeConversationId]: resetState
      }));
    }
  }, [activeConversationId]);

  // Preview handlers
  const handlePreview = useCallback(() => {
    updateChatState({ showPreview: true });
  }, [updateChatState]);

  const handleClosePreview = useCallback(() => {
    updateChatState({ showPreview: false });
  }, [updateChatState]);

  const handleShowEditableEmail = useCallback(() => {
    setShowEditableEmail(true);
  }, []);

  const handleCloseEditableEmail = useCallback(() => {
    setShowEditableEmail(false);
  }, []);

  const handleEdit = useCallback(() => {
    setSuccessMessage('Edit functionality will be available in the next update.');
  }, []);

  const handleSave = useCallback(() => {
    try {
      setSuccessMessage('Email template saved successfully!');
    } catch (error) {
      const contextError = error instanceof Error ? error.message : 'Failed to save template. Please try again.';
      updateChatState({ error: contextError });
    }
  }, [chatState.emailContent, updateChatState]);

  const handleCloseError = useCallback(() => {
    updateChatState({ error: null });
  }, [updateChatState]);

  const handleCloseSuccess = useCallback(() => {
    setSuccessMessage('');
  }, []);

  // Callback after template save and PUT API success to enable chat inputs
  const handleTemplateSaveSuccess = useCallback(async (templateId: string, conversationId: string) => {
    try {
      // Create AbortController for updateConversationState
      const updateAbortController = new AbortController();
      updateConversationStateAbortControllerRef.current = updateAbortController;
      
      const response = await updateConversationState({
        dealer_id: currentDealerId,
        conversation_id: conversationId,
        state: {
          generated_email_template_id: templateId
        }
      }, updateAbortController.signal);

      if (response?.success) {
        setIsChatInputDisabled(false);
        setSuccessMessage('Template saved and conversation updated successfully! You can continue chatting now.');
      } else {
        setError('Failed to update conversation state. Please try again.');
      }
    } catch (error) {
      console.error('Error updating conversation state:', error);
      setError('Failed to update conversation state. Please try again.');
    }
  }, [currentDealerId, updateConversationState]);

  // Sync context error with local error state
  useEffect(() => {
    if (error && error !== chatState.error) {
      updateChatState({ error });
    }
  }, [error, chatState.error, updateChatState]);

  // Handle dealer changes
  useEffect(() => {
    if (currentDealerId) {
      setChatState(initialChatState);
      setSuccessMessage('');
      setActiveConversationId('');
      setChatStates({});
      
      // Reconnect socket service for new dealer
      if (socketServiceRef.current) {
        socketServiceRef.current.disconnect();
        socketServiceRef.current.connect();
        setIsSocketConnected(socketServiceRef.current.isConnected());
      }
    }
  }, [currentDealerId]);

  // Monitor socket connection status
  useEffect(() => {
    const checkConnectionStatus = () => {
      if (socketServiceRef.current) {
        const connected = socketServiceRef.current.isConnected();
        if (connected !== isSocketConnected) {
          setIsSocketConnected(connected);
        }
      }
    };

    const interval = setInterval(checkConnectionStatus, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [isSocketConnected]);

  // Render content based on step
  const renderContent = () => {
    if (chatState.step === 0) {
      return (
        <Box sx={{ 
          display: 'flex', 
          height: '100%',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          <ChatHistory
            conversations={chatHistory}
            activeConversationId={activeConversationId}
            onConversationSelect={handleConversationSelect}
            onNewConversation={handleNewConversation}
            onDeleteConversation={handleDeleteConversation}
            onClearHistory={handleClearHistory}
          />
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minWidth: 0,
            overflow: 'hidden',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <OperationsPanel
              onPromptSelect={handleOperationSelect}
              loading={chatState.loading}
            />
          </Box>
        </Box>
      );
    }

    // Show main chat and preview interface
    return (
      <Box sx={{ 
        display: 'flex', 
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        <ChatHistory
          conversations={chatHistory}
          activeConversationId={activeConversationId}
          onConversationSelect={handleConversationSelect}
          onNewConversation={handleNewConversation}
          onDeleteConversation={handleDeleteConversation}
          onClearHistory={handleClearHistory}
        />
        <Box sx={{ 
          flex: 1, 
          // width: '100%',
          overflow: 'hidden',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <SplitPaneMAIQ
            left={
              <ChatPanel
                chatHistory={chatState.chatHistory}
                onOptionSelect={handleOptionSelect}
                onSendMessage={handleSendMessage}
                inputText={chatState.inputText}
                setInputText={(text) => updateChatState({ inputText: text })}
                loading={chatState.loading}
                onReset={handleReset}
                suggestions={chatState.suggestions}
                isInputRestricted={chatState.is_input_restricted_to_suggestions || isChatInputDisabled}
              />
            }
            right={
              <EmailContentPreview
                emailContent={chatState.emailContent}
                step={chatState.step}
                onPreview={handlePreview}
                onEdit={handleEdit}
                onSave={handleSave}
                showPreview={chatState.showPreview}
                onClosePreview={handleClosePreview}
                onShowEditableEmail={handleShowEditableEmail}
                showEditableEmail={showEditableEmail}
                onCloseEditableEmail={handleCloseEditableEmail}
                onEmailContentChange={(updatedContent) => {
                  setChatState(prev => ({
                    ...prev,
                    emailContent: updatedContent
                  }));
                }}
                onGeneratedEmailChange={(updatedEmail) => {
                  setGeneratedEmail(updatedEmail);
                }}
                isGenerating={chatState.loading}
                isGeneratingEmail={isGeneratingEmail}
                generatedEmail={generatedEmail}
                streamingData={streamingData}
                streamingText={streamingText}
                currentSection={currentSection}
                conversationId={activeConversationId}
                onTemplateSaveSuccess={handleTemplateSaveSuccess}
              />
            }
            defaultWidth={40}
            minWidth={10}
            maxWidth={90}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      height: 'calc(100vh - 346px)', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {renderContent()}

      {/* Error Snackbar */}
      <Snackbar
        open={!!chatState.error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {chatState.error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Socket Connection Status Snackbar */}
      <Snackbar
        open={!isSocketConnected && activeConversationId !== ''}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="warning" sx={{ width: '100%' }}>
          Socket disconnected - Real-time updates may not work
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GenerateTab;
